/**
 * V2 rebuild against Figma frames a_start_profile_setup (toggle off — show
 * email/password) and a_start_profile_setup_guest_account (toggle on — show
 * info card). Functional contract preserved verbatim: validation, register
 * call, error mapping, OrgPicker redirect for direct /register, displayName
 * sync at submit, pathKind init, navigation targets.
 */
import React, { useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import OnboardingShellV2 from '../components/OnboardingShellV2';
import OnboardingBrandHeader from '../components/OnboardingBrandHeader';
import ProgressIndicatorV2 from '../components/ProgressIndicatorV2';
import heroProfile from '../assets/onboarding/hero-profile.svg';
import toggleOnIcon from '../assets/icons/toggle_on.svg';
import toggleOffIcon from '../assets/icons/toggle_off.svg';
import infoIcon from '../assets/icons/info.svg';
import ClavisTextField from '../components/ClavisTextField';
import OrganizationPicker from './OrganizationPicker';
import { useWizardState } from '../wizard/useWizardState';
import { getStepInfo } from '../wizard/wizardSteps';
import { register as registerApi } from '../api/auth';
import { useAuth } from '../auth/useAuth';
import { ApiError } from '../api/client';
import { pillButtonFocusRing } from '../styles/onboardingButtons';

interface ProfileSetupStepProps {
  guestModeDefault?: boolean;
}

const RESERVED_NAMES = new Set(['admin', 'system', 'null', 'undefined']);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validatePasswordPolicy(password: string): boolean {
  if (password.length < 8) return false;
  let criteria = 0;
  if (/[A-Z]/.test(password)) criteria++;
  if (/[a-z]/.test(password)) criteria++;
  if (/[0-9]/.test(password)) criteria++;
  if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) criteria++;
  return criteria >= 3;
}

function nextStepAfterProfile(runCode: string | null): string {
  if (!runCode) return '/dashboard';
  return `/join/${runCode}/team/find`;
}

// Figma puts the Username label in Group 26 with bbox h=28, but the
// E-Mail and Password labels in Group 30 with bbox h=20. Same font / lh,
// different bbox-heights (bbox-height drift per figma-implement skill §6).
// The `bbox28` prop toggles the CSS padding-block-end +4 that matches
// Figma's taller bbox; default false (h=20) for E-Mail / Password.
const FieldLabel: React.FC<{
  children: React.ReactNode;
  htmlFor?: string;
  bbox28?: boolean;
}> = ({ children, htmlFor, bbox28 }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      fontFamily: "'Noto Sans', sans-serif",
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: 'text.primary',
      // Half-leading compensation (figma-implement skill §6 sub-drift):
      // body/large (Noto Sans 16/lh 24) has 3 px half-leading per side in
      // CSS line-box; Figma's text bbox is tight to the glyph at the top.
      // Pull element up so the glyph lands at Figma's y-coordinate.
      marginBlockStart: '-3px',
      // bbox-height drift (figma-implement skill §6 fourth-drift-source):
      // Only the Username label has Figma bbox h=28 (CSS lh=24, +4 padding
      // makes them match). E-Mail / Password labels are h=20 and need NO
      // padding extension.
      ...(bbox28 ? { paddingBlockEnd: '4px' } : null),
    }}
  >
    {children}
  </Typography>
);

const ProfileSetupStep: React.FC<ProfileSetupStepProps> = ({
  guestModeDefault,
}) => {
  const { t } = useTranslation('profileSetup');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { code: routeCode } = useParams<{ code?: string }>();

  // #222 — the org selection is bound to a `?org=<slug>` URL param, not to
  // sticky wizard/sessionStorage state. Selecting an org pushes a history
  // entry, so the browser back button returns to the picker instead of
  // skipping it. Guests never reach the picker (they carry a routeCode and the
  // org comes from the run), so this only affects the direct /register flow.
  const orgFromUrl = searchParams.get('org');
  const wizard = useWizardState();
  const { checkAuth } = useAuth();

  const initialGuest = guestModeDefault ?? Boolean(routeCode);
  const [guestMode, setGuestMode] = useState<boolean>(initialGuest);
  const [displayName, setDisplayName] = useState<string>(wizard.displayName);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (routeCode && wizard.pathKind !== 'with-code') {
      wizard.setPathKind('with-code');
      if (!wizard.runCode) wizard.setRunCode(routeCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCode]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    const trimmed = displayName.trim();
    if (trimmed.length < 2 || trimmed.length > 32) {
      next.displayName = t('usernameError.tooShort');
    } else if (RESERVED_NAMES.has(trimmed.toLowerCase())) {
      next.displayName = t('usernameError.reserved');
    }
    if (!guestMode) {
      if (!EMAIL_RE.test(email)) next.email = t('emailError.invalid');
      if (!validatePasswordPolicy(password)) {
        next.password = t('passwordError.tooWeak');
      }
      if (password !== passwordConfirm) {
        next.passwordConfirm = t('passwordConfirmError.mismatch');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onContinue = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (guestMode) {
        wizard.setDisplayName(displayName.trim());
        navigate({
          pathname: nextStepAfterProfile(wizard.runCode ?? routeCode ?? null),
          search: location.search,
        });
        return;
      }

      const slug = orgFromUrl ?? wizard.organizationSlug;
      if (!slug) {
        setErrors({ form: t('orgPicker.orgUnknown') });
        return;
      }
      try {
        const res = await registerApi({
          organization_slug: slug,
          email,
          password,
          display_name: displayName.trim(),
        });
        await checkAuth();
        wizard.setDisplayName(displayName.trim());
        if (res.requires_2fa) {
          navigate({ pathname: '/2fa-setup', search: location.search });
          return;
        }
        navigate({
          pathname: nextStepAfterProfile(wizard.runCode ?? routeCode ?? null),
          search: location.search,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          const detail = typeof err.detail === 'string' ? err.detail : '';
          if (err.status === 409 && /email/i.test(detail)) {
            setErrors({ email: t('emailError.taken') });
            return;
          }
          if (err.status === 409) {
            setErrors({ displayName: t('usernameError.taken') });
            return;
          }
          if (err.status === 400) {
            setErrors({ password: t('passwordError.tooWeak') });
            return;
          }
          if (err.status === 404) {
            setErrors({ form: t('orgPicker.orgNotFound') });
            return;
          }
        }
        setErrors({ form: t('orgPicker.registrationFailed') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const needsOrgPicker = !guestMode && !routeCode && !orgFromUrl;
  if (needsOrgPicker) {
    return (
      <OrganizationPicker
        onSelect={(org) => {
          // Mirror into wizard state (kept for consistency) and push the slug
          // into the URL so back-navigation returns to the picker (#222).
          wizard.setOrganizationSlug(org.slug);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('org', org.slug);
            return next;
          });
        }}
      />
    );
  }

  const showGuestToggle = Boolean(routeCode);
  const step = getStepInfo('profile-setup', wizard.pathKind);

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      {/* #383 — the profile mark, distinct from the OrganizationPicker's fox
          logo but rendered through the shared header so it's the SAME size and
          Y as every other /register step. Same position, different icon. */}
      <OnboardingBrandHeader src={heroProfile} />
      <Box
        component="form"
        noValidate
        // #343 — password fields must live inside a <form> so password
        // managers detect and offer to save the credentials. Submit is
        // intercepted; onContinue keeps handling navigation/registration.
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          void onContinue();
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
            // Half-leading compensation (figma-implement skill §6 sub-drift):
            // headline/small (Roboto 24/lh 32) has 2 px half-leading per side
            // in CSS line-box; Figma's text bbox is tight to the glyph at the
            // top. Pull element up so glyph lands at Figma's y-coordinate.
            marginBlockStart: '-2px',
          }}
        >
          {t('headline')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <FieldLabel htmlFor="profile-username" bbox28>{t('usernameLabel')}</FieldLabel>
          <ClavisTextField
            id="profile-username"
            autoComplete="nickname"
            placeholder={t('usernamePlaceholder')}
            helperText={errors.displayName ?? t('usernameHelper')}
            error={Boolean(errors.displayName)}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 32, 'aria-invalid': Boolean(errors.displayName) } }}
            fullWidth
          />
        </Box>

        {showGuestToggle && (
          // Figma frame_31: 298×40, row layout, gap 8.
          // Toggle is NOT a Switch component — it's a `toggle_on`/`toggle_off`
          // icon (40×40 outer, 24×24 viewBox) with a 37×20 track vector in
          // #1D47A4 (on) or #605D64 (off). Rendered via CSS-mask so we can
          // swap the track color per state.
          <Box
            component="button"
            type="button"
            role="switch"
            aria-checked={guestMode}
            aria-label={t('guestToggleLabel')}
            onClick={() => setGuestMode((g) => !g)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'flex-start',
              // Figma renders the supporting text "Your username is visible…"
              // outside the Group-29 bbox into the 24-px Frame-27 gap zone.
              // MUI keeps helper text inside the field, so pull this 11 px up
              // to land the visible gap to the supporting text at 13 px
              // (Figma visible-match).
              marginTop: '-11px',
              ...pillButtonFocusRing,
            }}
          >
            <Box
              component="img"
              src={guestMode ? toggleOnIcon : toggleOffIcon}
              alt=""
              aria-hidden
              sx={{
                width: '40px',
                height: '40px',
                flexShrink: 0,
                display: 'block',
              }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: "'Noto Sans', sans-serif",
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: '#322F35',
              }}
            >
              {t('guestToggleLabel')}
            </Box>
          </Box>
        )}

        <Collapse in={!guestMode} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel htmlFor="profile-email">{t('emailLabel')}</FieldLabel>
              <ClavisTextField
                id="profile-email"
                placeholder={t('emailPlaceholder')}
                type="email"
                autoComplete="email"
                helperText={errors.email}
                error={Boolean(errors.email)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel htmlFor="profile-password">{t('passwordLabel')}</FieldLabel>
              <ClavisTextField
                id="profile-password"
                placeholder={t('passwordPlaceholder')}
                type="password"
                passwordToggle
                showPasswordAriaLabel={tc('aria.showPassword')}
                hidePasswordAriaLabel={tc('aria.hidePassword')}
                autoComplete="new-password"
                helperText={errors.password}
                error={Boolean(errors.password)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel htmlFor="profile-password-confirm">{t('passwordConfirmLabel')}</FieldLabel>
              <ClavisTextField
                id="profile-password-confirm"
                type="password"
                passwordToggle
                showPasswordAriaLabel={tc('aria.showPassword')}
                hidePasswordAriaLabel={tc('aria.hidePassword')}
                autoComplete="new-password"
                helperText={errors.passwordConfirm}
                error={Boolean(errors.passwordConfirm)}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        </Collapse>

        <Collapse in={guestMode} unmountOnExit>
          <Box
            sx={{
              // Figma Message: 314×114, bg #E6E9F4, radius 22, padding 13/14/13/14, gap 12, stroke #FFFFFF@1.
              // iPhone-12-Audit: Figma's auto-layout pad-right is 14 but the
              // body "Hugs content" and overflows the pad-area by 5 px → the
              // EFFECTIVE body-right-margin in Figma is ~9, not 14. CSS lacks
              // overflow-on-Hug, so widen the content area by reducing
              // paddingRight from 14 to 9 — body now wraps to 3 lines like
              // Figma, not 4.
              backgroundColor: '#E6E9F4',
              border: '1px solid #FFFFFF',
              borderRadius: '22px',
              paddingBlock: '13px',
              paddingLeft: '14px',
              paddingRight: '9px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                // Figma chip: 76×18, bg #4D69B9, radius 8, padding 1/8/1/8, inner Frame 1 gap 8.
                alignSelf: 'flex-start',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#4D69B9',
                color: '#FFFFFF',
                borderRadius: '8px',
                paddingInline: '8px',
                paddingBlock: '1px',
                fontFamily: "'Noto Sans', sans-serif",
                fontSize: '11px',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '0.5px',
              }}
            >
              <Box
                component="img"
                src={infoIcon}
                alt=""
                aria-hidden
                sx={{
                  // Figma info-icon: 12×12 outer (inner Vector 10×10 #FFFFFF).
                  width: '12px',
                  height: '12px',
                  flexShrink: 0,
                  display: 'block',
                }}
              />
              {tc('infoBadge')}
            </Box>
            <Typography
              sx={{
                fontFamily: "'Noto Sans', sans-serif",
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.25px',
                color: '#00348D',
              }}
            >
              {t('guestInfoBox.body')}
            </Typography>
          </Box>
        </Collapse>

        {errors.form && (
          <Typography
            role="alert"
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              color: 'error.main',
            }}
          >
            {errors.form}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            // Mode-conditional comp (figma-implement skill §6 sixth-drift):
            // Figma's password & email TFs have `Show supporting text=False`
            // → no helper renders → no overflow. Action row's natural CSS Y
            // = Figma Frame 34 bbox top (633). To land pill-top at Figma's
            // visible pill-top (637, +4 inside bbox slack), add +4. In GUEST
            // mode the last sibling is Info-Box (no overflow either) and the
            // box height delta is absorbed upstream → 0 works.
            marginBlockStart: guestMode ? '0px' : '4px',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            disabled={submitting}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: submitting ? 'not-allowed' : 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              // Figma back button: stroke #CAC4D0@1 strokeAlign=INSIDE.
              // Use inset box-shadow instead of CSS `border`, otherwise the
              // pill inflates from 40 → 42 px tall and 2 px wider (CSS
              // `border` adds outside the content box).
              boxShadow: 'inset 0 0 0 1px var(--color-outline-variant, #CAC4D0)',
              color: 'text.secondary',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
              ...pillButtonFocusRing,
            }}
          >
            {tc('actions.back', { defaultValue: 'Back' })}
          </Box>
          <Box
            component="button"
            type="submit"
            disabled={submitting}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              backgroundColor: '#4D69B9',
              color: '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#3D54A0' },
              ...pillButtonFocusRing,
            }}
          >
            {submitting ? t('submitting') : tc('actions.continue', { defaultValue: 'Continue' })}
          </Box>
        </Box>
      </Box>
    </OnboardingShellV2>
  );
};

export default ProfileSetupStep;
